import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';

// Helper function for lazy loading with named chunks
const lazyLoad = (viewPath: string) => {
  // Wir verwenden explizite Import-Pfade statt Template-Strings
  // Dies löst das Problem mit dynamischen Imports in Vite
  try {
    switch (viewPath) {
      // Home
      case 'home/Home':
        return () => import(/* webpackChunkName: "view-home" */ '../views/home/Home.vue');
      
      // Admin
      case 'admin/AdminDashboard':
        return () => import(/* webpackChunkName: "view-admin" */ '../views/admin/AdminDashboard.vue');
      case 'admin/ToolManagement':
        return () => import(/* webpackChunkName: "view-admin-tools" */ '../views/admin/ToolManagement.vue');
      case 'admin/UserManagement':
        return () => import(/* webpackChunkName: "view-admin-users" */ '../views/admin/UserManagement.vue');
      case 'admin/UserPermissions':
        return () => import(/* webpackChunkName: "view-admin-permissions" */ '../views/admin/UserPermissions.vue');
      case 'admin/AuditLogs':
        return () => import(/* webpackChunkName: "view-admin-audit-logs" */ '../views/admin/AuditLogs.vue');
      case 'admin/GroupManagement':
        return () => import(/* webpackChunkName: "view-admin-groups" */ '../views/admin/GroupManagement.vue');
      case 'admin/CredentialManagement':
        return () => import(/* webpackChunkName: "view-admin-credentials" */ '../views/admin/CredentialManagement.vue');
      case 'admin/EmailManagement':
        return () => import(/* webpackChunkName: "view-admin-email" */ '../views/admin/EmailManagement.vue');
      case 'admin/tools/ToolSettings':
        return () => import(/* webpackChunkName: "view-admin-tool-settings" */ '../views/admin/tools/ToolSettings.vue');
      
      // Tools - ToccoGen
      case 'tools/toccogen/Dashboard':
        return () => import(/* webpackChunkName: "view-toccogen" */ '../views/tools/toccogen/Dashboard.vue');
      case 'tools/toccogen/Settings':
        return () => import(/* webpackChunkName: "view-toccogen-settings" */ '../views/tools/toccogen/Settings.vue');
      case 'tools/toccogen/components/ExamplesComponent':
        return () => import(/* webpackChunkName: "view-toccogen-examples" */ '../views/tools/toccogen/components/ExamplesComponent.vue');
      case 'tools/toccogen/components/DocumentationComponent':
        return () => import(/* webpackChunkName: "view-toccogen-documentation" */ '../views/tools/toccogen/components/DocumentationComponent.vue');
      
      // Tools - UEK Umbuchungsgebühren
      case 'tools/uek_umbuchungsgebuehren/uek_umbuchungsgebuehrenDashboard':
        return () => import(/* webpackChunkName: "view-uek-umbuchungsgebuehren" */ '../views/tools/uek_umbuchungsgebuehren/uek_umbuchungsgebuehrenDashboard.vue');
      
      // Tools - Subventions Vergleich
      case 'tools/subventions_vergleich/subventions_vergleichDashboard':
        return () => import(/* webpackChunkName: "view-subventions-vergleich" */ '../views/tools/subventions_vergleich/subventions_vergleichDashboard.vue');
      case 'tools/subventions_vergleich/PlzListe':
        return () => import(/* webpackChunkName: "view-plz-liste" */ '../views/tools/subventions_vergleich/PlzListe.vue');
      
      // Tools - LC Verwaltung
      case 'tools/lc_verwaltung/lc_verwaltungDashboard':
        return () => import(/* webpackChunkName: "view-lc-verwaltung" */ '../views/tools/lc_verwaltung/lc_verwaltungDashboard.vue');
      case 'tools/lc_verwaltung/lc_verwaltungClassMapping':
        return () => import(/* webpackChunkName: "view-lc-class-mapping" */ '../views/tools/lc_verwaltung/lc_verwaltungClassMapping.vue');
      case 'tools/lc_verwaltung/lc_verwaltungSyncLogs':
        return () => import(/* webpackChunkName: "view-lc-sync-logs" */ '../views/tools/lc_verwaltung/lc_verwaltungSyncLogs.vue');
      
      // Tools - LVA Export
      case 'tools/lva_export/lva_exportDashboard':
        return () => import(/* webpackChunkName: "view-lva-export" */ '../views/tools/lva_export/lva_exportDashboard.vue');
      case 'tools/lva_export/lva_exportSchedules':
        return () => import(/* webpackChunkName: "view-lva-export-schedules" */ '../views/tools/lva_export/lva_exportSchedules.vue');
      case 'tools/lva_export/lva_exportRuns':
        return () => import(/* webpackChunkName: "view-lva-export-runs" */ '../views/tools/lva_export/lva_exportRuns.vue');

      // Tools - Lohn Dozenteneinsatz
      case 'tools/lohn_dozenteneinsatz/lohn_dozenteneinsatzDashboard':
        return () => import(/* webpackChunkName: "view-lohn-dozenteneinsatz" */ '../views/tools/lohn_dozenteneinsatz/lohn_dozenteneinsatzDashboard.vue');
      case 'tools/lohn_dozenteneinsatz/lohn_dozenteneinsatzDataView':
        return () => import(/* webpackChunkName: "view-lohn-dozenteneinsatz-schedules" */ '../views/tools/lohn_dozenteneinsatz/lohn_dozenteneinsatzDataView.vue');
      case 'tools/lohn_dozenteneinsatz/lohn_dozenteneinsatzRuns':
        return () => import(/* webpackChunkName: "view-lohn-dozenteneinsatz-runs" */ '../views/tools/lohn_dozenteneinsatz/lohn_dozenteneinsatzRuns.vue');

      // Tools - Event Templates
      case 'tools/event_templates/event_templatesDashboard':
        return () => import(/* webpackChunkName: "view-event-templates" */ '../views/tools/event_templates/event_templatesDashboard.vue');
      case 'tools/event_templates/TemplatesView':
        return () => import(/* webpackChunkName: "view-event-templates-list" */ '../views/tools/event_templates/TemplatesView.vue');
      case 'tools/event_templates/ImportView':
        return () => import(/* webpackChunkName: "view-event-templates-import" */ '../views/tools/event_templates/ImportView.vue');
      case 'tools/event_templates/HistoryView':
        return () => import(/* webpackChunkName: "view-event-templates-history" */ '../views/tools/event_templates/HistoryView.vue');
      case 'tools/event_templates/DiagnosticsView':
        return () => import(/* webpackChunkName: "view-event-templates-diagnostics" */ '../views/tools/event_templates/DiagnosticsView.vue');
      
      // Tools - OdAssio Chatbot
      case 'tools/odassio_chatbot/odassio_chatbotDashboard':
        return () => import(/* webpackChunkName: "view-odassio-chatbot" */ '../views/tools/odassio_chatbot/odassio_chatbotDashboard.vue');
      
      // Tools - SharePoint Permissions
      case 'tools/sharepoint_permissions/SharepointPermissionsDashboard':
        return () => import(/* webpackChunkName: "view-sharepoint-permissions" */ '../views/tools/sharepoint_permissions/SharepointPermissionsDashboard.vue');
      case 'tools/sharepoint_permissions/sharepoint_permissionsDataView':
        return () => import(/* webpackChunkName: "view-sharepoint-permissions-data" */ '../views/tools/sharepoint_permissions/sharepoint_permissionsDataView.vue');
      case 'tools/sharepoint_permissions/SharepointSkipListView':
        return () => import(/* webpackChunkName: "view-sharepoint-permissions-skiplist" */ '../views/tools/sharepoint_permissions/SharepointSkipListView.vue');
      case 'tools/odassio_chatbot/odassio_chatbotChats':
        return () => import(/* webpackChunkName: "view-odassio-chatbot-chats" */ '../views/tools/odassio_chatbot/odassio_chatbotChats.vue');
      case 'tools/odassio_chatbot/odassio_chatbotAuswertungen':
        return () => import(/* webpackChunkName: "view-odassio-chatbot-auswertungen" */ '../views/tools/odassio_chatbot/odassio_chatbotAuswertungen.vue');
      case 'tools/odassio_chatbot/odassio_chatbotTriggers':
        return () => import(/* webpackChunkName: "view-odassio-chatbot-triggers" */ '../views/tools/odassio_chatbot/odassio_chatbotTriggers.vue');
      case 'tools/odassio_chatbot/odassio_chatbotHotlineBewertung':
        return () => import(/* webpackChunkName: "view-odassio-hotline-bewertung" */ '../views/tools/odassio_chatbot/odassio_chatbotHotlineBewertung.vue');
      
      // Tools - AZEPA Zeiterfassung
      case 'tools/arbeitszeiterfassung_pa/arbeitszeiterfassung_paDashboard':
        return () => import(/* webpackChunkName: "view-azepa-dashboard" */ '../views/tools/arbeitszeiterfassung_pa/arbeitszeiterfassung_paDashboard.vue');
      case 'tools/arbeitszeiterfassung_pa/Zeiterfassung':
        return () => import(/* webpackChunkName: "view-azepa-zeiterfassung" */ '../views/tools/arbeitszeiterfassung_pa/Zeiterfassung.vue');
      case 'tools/arbeitszeiterfassung_pa/Studenten':
        return () => import(/* webpackChunkName: "view-azepa-studenten" */ '../views/tools/arbeitszeiterfassung_pa/Studenten.vue');
      case 'tools/arbeitszeiterfassung_pa/Katalog':
        return () => import(/* webpackChunkName: "view-azepa-katalog" */ '../views/tools/arbeitszeiterfassung_pa/Katalog.vue');
      case 'tools/arbeitszeiterfassung_pa/Quartale':
        return () => import(/* webpackChunkName: "view-azepa-quartale" */ '../views/tools/arbeitszeiterfassung_pa/Quartale.vue');
      
      // Auth
      case 'auth/Login':
        return () => import(/* webpackChunkName: "view-login" */ '../views/auth/Login.vue');
      case 'auth/Callback':
        return () => import(/* webpackChunkName: "view-callback" */ '../views/auth/Callback.vue');
            
      // Fallback
      default:
        console.warn(`Unbekannter View-Pfad: ${viewPath} - Verwende NotFound-Komponente als Fallback`);
        return () => import(/* webpackChunkName: "view-not-found" */ '../views/NotFound.vue');
    }
  } catch (error) {
    console.error(`Fehler beim Laden von ${viewPath}:`, error);
    return () => import(/* webpackChunkName: "view-error" */ '../views/NotFound.vue');
  }
};

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    component: () => import(/* webpackChunkName: "layout-app" */ '../layouts/AppLayout.vue'),
    children: [
      {
        path: '',
        name: 'home',
        component: lazyLoad('home/Home')
      },
      {
        path: 'admin',
        name: 'admin',
        component: lazyLoad('admin/AdminDashboard'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'admin/users',
        name: 'admin-users',
        component: lazyLoad('admin/UserManagement'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'admin/tools',
        name: 'admin-tools',
        component: lazyLoad('admin/ToolManagement'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'admin/permissions',
        name: 'admin-permissions',
        component: lazyLoad('admin/UserPermissions'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'admin/audit-logs',
        name: 'admin-audit-logs',
        component: lazyLoad('admin/AuditLogs'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'admin/groups',
        name: 'admin-groups',
        component: lazyLoad('admin/GroupManagement'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'admin/credentials',
        name: 'admin-credentials',
        component: lazyLoad('admin/CredentialManagement'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'admin/email',
        name: 'admin-email',
        component: lazyLoad('admin/EmailManagement'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'admin/tools/:toolId/settings',
        name: 'admin-tool-settings',
        component: lazyLoad('admin/tools/ToolSettings'),
        meta: { requiresAdmin: true }
      },
      {
        path: 'tools/toccogen',
        name: 'toccogen',
        component: lazyLoad('tools/toccogen/Dashboard'),
        meta: { requiresAuth: true }
      },
      {
        path: 'tools/toccogen/settings',
        name: 'toccogen-settings',
        component: lazyLoad('tools/toccogen/Settings'),
        meta: { requiresAuth: true }
      },
      {
        path: 'tools/toccogen/examples',
        name: 'toccogen-examples',
        component: lazyLoad('tools/toccogen/components/ExamplesComponent'),
        meta: { requiresAuth: true }
      },
      {
        path: 'tools/toccogen/documentation',
        name: 'toccogen-documentation',
        component: lazyLoad('tools/toccogen/components/DocumentationComponent'),
        meta: { requiresAuth: true }
      },
      {
        path: 'tools/uek_umbuchungsgebuehren',
        name: 'uek_umbuchungsgebuehren',
        component: lazyLoad('tools/uek_umbuchungsgebuehren/uek_umbuchungsgebuehrenDashboard'),
        meta: { 
          requiresAuth: true,
          toolPermission: 'uek_umbuchungsgebuehren'
        }
      },
      {
        path: 'tools/subventions_vergleich',
        name: 'subventions_vergleich',
        component: lazyLoad('tools/subventions_vergleich/subventions_vergleichDashboard'),
        meta: { 
          requiresAuth: true,
          toolPermission: 'subventions_vergleich'
        }
      },
      {
        path: 'tools/subventions_vergleich/plz-liste',
        name: 'plz-liste',
        component: lazyLoad('tools/subventions_vergleich/PlzListe'),
        meta: { 
          requiresAuth: true,
          toolPermission: 'subventions_vergleich'
        }
      },
      {
        path: 'tools/lc_verwaltung',
        name: 'lc_verwaltung',
        component: lazyLoad('tools/lc_verwaltung/lc_verwaltungDashboard'),
        meta: { 
          requiresAuth: true,
          toolPermission: 'lc_verwaltung'
        }
      },
      {
        path: 'tools/lc_verwaltung/class-mapping',
        name: 'lc_verwaltung_class_mapping',
        component: lazyLoad('tools/lc_verwaltung/lc_verwaltungClassMapping'),
        meta: { 
          requiresAuth: true,
          toolPermission: 'lc_verwaltung'
        }
      },
      {
        path: 'tools/lc_verwaltung/sync-logs',
        name: 'lc_verwaltung_sync_logs',
        component: lazyLoad('tools/lc_verwaltung/lc_verwaltungSyncLogs'),
        meta: { 
          requiresAuth: true,
          toolPermission: 'lc_verwaltung'
        }
      },
      {
        path: 'tools/lva_export',
        name: 'lva_export',
        component: lazyLoad('tools/lva_export/lva_exportDashboard'),
        meta: {
          requiresAuth: true,
          toolPermission: 'lva_export'
        }
      },
      {
        path: 'tools/lva_export/schedules',
        name: 'lva_export_schedules',
        component: lazyLoad('tools/lva_export/lva_exportSchedules'),
        meta: {
          requiresAuth: true,
          toolPermission: 'lva_export'
        }
      },
      {
        path: 'tools/lva_export/runs',
        name: 'lva_export_runs',
        component: lazyLoad('tools/lva_export/lva_exportRuns'),
        meta: {
          requiresAuth: true,
          toolPermission: 'lva_export'
        }
      },
      {
        path: 'tools/lohn_dozenteneinsatz',
        name: 'lohn_dozenteneinsatz',
        component: lazyLoad('tools/lohn_dozenteneinsatz/lohn_dozenteneinsatzDashboard'),
        meta: {
          requiresAuth: true,
          toolPermission: 'lohn_dozenteneinsatz'
        }
      },
      {
        path: 'tools/lohn_dozenteneinsatz/schedules',
        name: 'lohn_dozenteneinsatz_schedules',
        component: lazyLoad('tools/lohn_dozenteneinsatz/lohn_dozenteneinsatzDataView'),
        meta: {
          requiresAuth: true,
          toolPermission: 'lohn_dozenteneinsatz'
        }
      },
      {
        path: 'tools/lohn_dozenteneinsatz/runs',
        name: 'lohn_dozenteneinsatz_runs',
        component: lazyLoad('tools/lohn_dozenteneinsatz/lohn_dozenteneinsatzRuns'),
        meta: {
          requiresAuth: true,
          toolPermission: 'lohn_dozenteneinsatz'
        }
      },
      {
        path: 'tools/event_templates',
        name: 'event_templates',
        component: lazyLoad('tools/event_templates/event_templatesDashboard'),
        meta: {
          requiresAuth: true,
          toolPermission: 'event_templates'
        }
      },
      {
        path: 'tools/event_templates/templates',
        name: 'event_templates_list',
        component: lazyLoad('tools/event_templates/TemplatesView'),
        meta: {
          requiresAuth: true,
          toolPermission: 'event_templates'
        }
      },
      {
        path: 'tools/event_templates/import',
        name: 'event_templates_import',
        component: lazyLoad('tools/event_templates/ImportView'),
        meta: {
          requiresAuth: true,
          toolPermission: 'event_templates'
        }
      },
      {
        path: 'tools/event_templates/history',
        name: 'event_templates_history',
        component: lazyLoad('tools/event_templates/HistoryView'),
        meta: {
          requiresAuth: true,
          toolPermission: 'event_templates'
        }
      },
      {
        path: 'tools/event_templates/diagnostics',
        name: 'event_templates_diagnostics',
        component: lazyLoad('tools/event_templates/DiagnosticsView'),
        meta: {
          requiresAuth: true,
          toolPermission: 'event_templates'
        }
      },
      {
        path: 'tools/odassio_chatbot',
        name: 'odassio_chatbot',
        component: lazyLoad('tools/odassio_chatbot/odassio_chatbotDashboard'),
        meta: { 
          requiresAuth: true,
          toolPermission: 'odassio_chatbot'
        }
      },
      {
        path: 'tools/odassio_chatbot/chats',
        name: 'odassio_chatbot_chats',
        component: lazyLoad('tools/odassio_chatbot/odassio_chatbotChats'),
        meta: { 
          requiresAuth: true,
          toolPermission: 'odassio_chatbot'
        }
      },
      {
        path: 'tools/odassio_chatbot/auswertungen',
        name: 'odassio_chatbot_auswertungen',
        component: lazyLoad('tools/odassio_chatbot/odassio_chatbotAuswertungen'),
        meta: { 
          requiresAuth: true,
          toolPermission: 'odassio_chatbot'
        }
      },
      {
        path: 'tools/odassio_chatbot/triggers',
        name: 'odassio_chatbot_triggers',
        component: lazyLoad('tools/odassio_chatbot/odassio_chatbotTriggers'),
        meta: { 
          requiresAuth: true,
          toolPermission: 'odassio_chatbot'
        }
      },
      {
        path: 'tools/odassio_chatbot/hotline-bewertung',
        name: 'odassio_chatbot_hotline_bewertung',
        component: lazyLoad('tools/odassio_chatbot/odassio_chatbotHotlineBewertung'),
        meta: { 
          requiresAuth: true,
          toolPermission: 'odassio_chatbot'
        }
      },
      {
        path: 'tools/sharepoint_permissions',
        name: 'sharepoint_permissions',
        component: lazyLoad('tools/sharepoint_permissions/SharepointPermissionsDashboard'),
        meta: { 
          requiresAuth: true,
          toolPermission: 'sharepoint_permissions'
        }
      },
      {
        path: 'tools/sharepoint_permissions/data',
        name: 'sharepoint_permissions_data',
        component: lazyLoad('tools/sharepoint_permissions/sharepoint_permissionsDataView'),
        meta: { 
          requiresAuth: true,
          toolPermission: 'sharepoint_permissions'
        }
      },
      {
        path: 'tools/sharepoint_permissions/skip-list',
        name: 'sharepoint_permissions_skip_list',
        component: lazyLoad('tools/sharepoint_permissions/SharepointSkipListView'),
        meta: {
          requiresAuth: true,
          toolPermission: 'sharepoint_permissions'
        }
      },
      {
        path: 'tools/arbeitszeiterfassung_pa',
        name: 'arbeitszeiterfassung_pa',
        component: lazyLoad('tools/arbeitszeiterfassung_pa/arbeitszeiterfassung_paDashboard'),
        meta: { 
          requiresAuth: true,
          toolPermission: 'arbeitszeiterfassung_pa'
        }
      },
      {
        path: 'tools/arbeitszeiterfassung_pa/zeiterfassung',
        name: 'arbeitszeiterfassung_pa_zeiterfassung',
        component: lazyLoad('tools/arbeitszeiterfassung_pa/Zeiterfassung'),
        meta: { 
          requiresAuth: true,
          toolPermission: 'arbeitszeiterfassung_pa'
        }
      },
      {
        path: 'tools/arbeitszeiterfassung_pa/studenten',
        name: 'arbeitszeiterfassung_pa_studenten',
        component: lazyLoad('tools/arbeitszeiterfassung_pa/Studenten'),
        meta: { 
          requiresAuth: true,
          toolPermission: 'arbeitszeiterfassung_pa'
        }
      },
      {
        path: 'tools/arbeitszeiterfassung_pa/katalog',
        name: 'arbeitszeiterfassung_pa_katalog',
        component: lazyLoad('tools/arbeitszeiterfassung_pa/Katalog'),
        meta: { 
          requiresAuth: true,
          toolPermission: 'arbeitszeiterfassung_pa'
        }
      },
      {
        path: 'tools/arbeitszeiterfassung_pa/quartale',
        name: 'arbeitszeiterfassung_pa_quartale',
        component: lazyLoad('tools/arbeitszeiterfassung_pa/Quartale'),
        meta: { 
          requiresAuth: true,
          toolPermission: 'arbeitszeiterfassung_pa'
        }
      },
      
      // Tools werden dynamisch über das Tool-Management-System generiert
    ]
  },
  {
    path: '/auth',
    children: [
      {
        path: 'login',
        name: 'login',
        component: lazyLoad('auth/Login')
      },
      {
        path: 'callback',
        name: 'oauth-callback',
        component: lazyLoad('auth/Callback')
      }
    ]
  },
  {
    path: '/login',
    name: 'login',
    component: lazyLoad('auth/Login')
  },
  {
    path: '/auth/callback',
    name: 'auth-callback',
    component: lazyLoad('auth/Callback'),
    meta: { 
      layout: 'none' // Kein Layout-Wrapper, damit Callback eigenes Home-Layout verwenden kann
    }
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes
});

// Navigation Guards für Authentifizierung
import { useAuthStore } from '../stores/auth';
import { useToolsStore } from '../stores/tools';

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore();
  const toolsStore = useToolsStore();

  // Callback-Routen immer erlauben
  if (to.name === 'oauth-callback' || to.name === 'auth-callback') {
    next();
    return;
  }

  // Auth-Bedarf der Route bestimmen
  const requiresAuth  = !!(to.meta.requiresAuth || to.meta.requiresAdmin || to.meta.toolPermission);
  const requiresAdmin = !!to.meta.requiresAdmin;

  // Session validieren: Pinia-State oder localStorage wiederherstellen
  if (!authStore.isAuthenticated) {
    try {
      await authStore.restoreAuthState();
    } catch {
      // restoreAuthState ruft clearAuth() intern auf
    }
  }

  // Auth prüfen
  if (requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'login' });
    return;
  }

  // Admin prüfen
  if (requiresAdmin && !authStore.isAdmin) {
    next({ name: 'home' });
    return;
  }

  // Tool-Berechtigungen prüfen
  if (to.meta.toolPermission && authStore.isAuthenticated) {
    try {
      await toolsStore.loadToolsAndPermissions();

      const toolName = to.meta.toolPermission as string;
      let hasPermission = toolsStore.hasToolPermission(toolName, 'view');

      // Fallback: per tool_path suchen
      if (!hasPermission) {
        const matchingTool = toolsStore.tools.find(t => t.name === toolName || t.path === toolName);
        if (matchingTool) {
          hasPermission = toolsStore.hasToolPermission(matchingTool.path, 'view');
        }
      }

      // Admins haben immer Zugriff
      if (!hasPermission && authStore.isAdmin) {
        hasPermission = true;
      }

      if (!hasPermission) {
        next({ name: 'home' });
        return;
      }
    } catch {
      next({ name: 'home' });
      return;
    }
  }

  next();
});

export default router;
